import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, LineChart, Line
} from 'recharts';
import { FiCheckCircle, FiCircle, FiAlertCircle, FiList, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { format, isBefore, startOfDay, subDays, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    return new Date(d);
};

// Custom Tick Component for User Avatars
const CustomUserTick = ({ x, y, payload, users, tasksByAssignee }) => {
    // Find the full user data based on the name (payload.value)
    // We used the name as the key in the data processing, which might be ambiguous.
    // Better to match by the filtered data list we passed to the chart, but payload.value gives the label.
    // Let's try to find the user from the 'users' prop matching the displayName.

    // However, simplest way given the limitation: try to find user by matching displayName or email.
    // Or, pass the email as the key in data, and format the tick to show Name + Avatar.

    // Let's find the assignee item from our prepared data to get the email/data if possible,
    // but here we only get payload.value (the name).

    // We'll search in the 'users' list.
    const user = users.find(u => (u.displayName || u.email) === payload.value);
    const photoURL = user?.photoURL;
    const initial = payload.value ? payload.value.charAt(0).toUpperCase() : '?';

    return (
        <g transform={`translate(${x},${y})`}>
            {photoURL ? (
                <image x={-12} y={5} href={photoURL} width={24} height={24} style={{ clipPath: 'circle(12px at 12px 12px)' }} preserveAspectRatio="xMidYMid slice" />
            ) : (
                <circle cx={0} cy={17} r={12} fill="#3B82F6" />
            )}
            {!photoURL && (
                <text x={0} y={21} textAnchor="middle" fill="#fff" fontSize={10} fontWeight="bold">{initial}</text>
            )}
            <text x={0} y={45} dy={0} textAnchor="middle" fill="#6B7280" fontSize={10} width={50} style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {payload.value.split(' ')[0]}
            </text>
        </g>
    );
};

const DashboardView = ({ tasks, sections, users }) => {

    // Helper: Flatten tasks and subtasks recursively into a single list
    const allTasks = useMemo(() => {
        const flat = [];
        const flattenSubs = (subs, parentAsignados, parentSection, parentFecha) => {
            (subs || []).forEach(sub => {
                const inherited = (sub.asignados && sub.asignados.length > 0) ? sub.asignados : parentAsignados;
                flat.push({
                    ...sub,
                    type: 'subtask',
                    effectiveSectionId: parentSection,
                    asignados: inherited,
                    fechaVencimiento: sub.fechaVencimiento || parentFecha
                });
                if (sub.subtareas && sub.subtareas.length > 0) {
                    flattenSubs(sub.subtareas, inherited, parentSection, sub.fechaVencimiento || parentFecha);
                }
            });
        };
        tasks.forEach(task => {
            flat.push({
                ...task,
                type: 'task',
                effectiveSectionId: task.seccionId
            });
            flattenSubs(task.subtareas, task.asignados || [], task.seccionId, task.fechaVencimiento);
        });
        return flat;
    }, [tasks]);

    const metrics = useMemo(() => {
        const total = allTasks.length;
        const completed = allTasks.filter(t => t.estado === 'finalizado').length;
        const incomplete = total - completed;

        const today = startOfDay(new Date());
        const overdue = allTasks.filter(t =>
            t.estado !== 'finalizado' &&
            t.fechaVencimiento &&
            isBefore(parseDate(t.fechaVencimiento), today)
        ).length;

        return { total, completed, incomplete, overdue };
    }, [allTasks]);

    const tasksBySection = useMemo(() => {
        return sections.map(section => {
            // Count all items (tasks + subtasks) in this section
            const count = allTasks.filter(t =>
                t.effectiveSectionId === section.id &&
                t.estado !== 'finalizado'
            ).length;

            return {
                name: section.titulo,
                count: count
            };
        }).filter(item => item.count > 0);
    }, [allTasks, sections]);

    const tasksByStatus = useMemo(() => {
        // Count statuses from ALL items
        const completed = allTasks.filter(t => t.estado === 'finalizado').length;
        const notCompleted = allTasks.length - completed;

        return [
            { name: 'Finalizadas', value: completed },
            { name: 'Sin finalizar', value: notCompleted }
        ];
    }, [allTasks]);

    const tasksByAssignee = useMemo(() => {
        const counts = {};
        // Filter incomplete tasks/subtasks
        allTasks.filter(t => t.estado !== 'finalizado').forEach(item => {
            if (item.asignados && item.asignados.length > 0) {
                item.asignados.forEach(email => {
                    const user = users.find(u => u.email === email);
                    const name = user ? (user.displayName || email) : email;
                    counts[name] = (counts[name] || 0) + 1;
                });
            } else {
                counts['Sin asignar'] = (counts['Sin asignar'] || 0) + 1;
            }
        });

        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }, [allTasks, users]);

    const tasksOverTime = useMemo(() => {
        // Using allTasks for timeline as well
        const sorted = [...allTasks]
            .filter(t => t.fechaVencimiento)
            .sort((a, b) => parseDate(a.fechaVencimiento) - parseDate(b.fechaVencimiento));

        const grouped = {};
        sorted.forEach(t => {
            const date = format(parseDate(t.fechaVencimiento), 'dd/MM', { locale: es });
            grouped[date] = (grouped[date] || 0) + 1;
        });

        return Object.entries(grouped).map(([date, count]) => ({ date, count }));
    }, [allTasks]);

    // Burndown chart data: shows remaining tasks over the last 30 days
    const burndownData = useMemo(() => {
        const today = new Date();
        const days = 30;
        const data = [];
        for (let i = days; i >= 0; i--) {
            const date = subDays(today, i);
            const dateStart = startOfDay(date);
            // Count tasks created before or on this date that were NOT completed by this date
            const remaining = allTasks.filter(t => {
                const created = parseDate(t.fechaCreacion);
                if (!created || created > dateStart) return false;
                // Check if completed, and if completed before this date
                if (t.estado === 'finalizado') {
                    const activity = (t.actividad || []).find(a => a.campo === 'estado' && a.detalles?.includes('Finalizado'));
                    if (activity) {
                        const completedDate = new Date(activity.fecha);
                        return completedDate > dateStart;
                    }
                    return false; // Completed but no activity log - assume completed
                }
                return true;
            }).length;
            data.push({ date: format(date, 'd MMM', { locale: es }), remaining });
        }
        return data;
    }, [allTasks]);

    // Velocity: tasks completed per week over last 8 weeks
    const velocityData = useMemo(() => {
        const today = new Date();
        const weeks = [];
        for (let w = 7; w >= 0; w--) {
            const weekEnd = subDays(today, w * 7);
            const weekStart = subDays(weekEnd, 6);
            const completed = allTasks.filter(t => {
                if (t.estado !== 'finalizado') return false;
                const activity = (t.actividad || []).find(a => a.campo === 'estado' && a.detalles?.includes('Finalizado'));
                if (activity) {
                    const d = new Date(activity.fecha);
                    return d >= startOfDay(weekStart) && d <= weekEnd;
                }
                return false;
            }).length;
            weeks.push({
                week: format(weekStart, 'd MMM', { locale: es }),
                completadas: completed,
            });
        }
        return weeks;
    }, [allTasks]);

    // Priority distribution
    const priorityData = useMemo(() => {
        const counts = { alta: 0, media: 0, baja: 0 };
        allTasks.filter(t => t.estado !== 'finalizado').forEach(t => {
            const p = t.prioridad || 'media';
            if (p in counts) counts[p]++;
        });
        return [
            { name: 'Alta', value: counts.alta, color: '#EF4444' },
            { name: 'Media', value: counts.media, color: '#F59E0B' },
            { name: 'Baja', value: counts.baja, color: '#3B82F6' },
        ];
    }, [allTasks]);

    // Completion rate
    const completionRate = metrics.total > 0 ? Math.round((metrics.completed / metrics.total) * 100) : 0;

    return (
        <div className="p-3 sm:p-6 bg-gray-50 h-full overflow-y-auto">

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-sm mb-1">Total de tareas finalizadas</span>
                    <span className="text-4xl font-light text-gray-800">{metrics.completed}</span>
                    <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                        <FiCheckCircle /> <span>Tareas completadas (incl. subtareas)</span>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-sm mb-1">Total de tareas sin finalizar</span>
                    <span className="text-4xl font-light text-gray-800">{metrics.incomplete}</span>
                    <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                        <FiCircle /> <span>Pendientes</span>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-sm mb-1">Total de tareas con retraso</span>
                    <span className="text-4xl font-light text-gray-800">{metrics.overdue}</span>
                    <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                        <FiAlertCircle /> <span>Vencidas</span>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-sm mb-1">Tareas en total</span>
                    <span className="text-4xl font-light text-gray-800">{metrics.total}</span>
                    <div className="mt-2 text-xs text-blue-500 flex items-center gap-1">
                        <FiList /> <span>Global</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

                {/* Tasks by Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-6">Total de tareas sin finalizar por sección</h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={tasksBySection} layout="horizontal" margin={{ bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fill: '#9CA3AF' }}
                                    interval={0}
                                    angle={-15}
                                    textAnchor="end"
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                <Tooltip cursor={{ fill: '#f9fafb' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                <Bar dataKey="count" fill="#818CF8" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Tasks by Status */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-6">Total de tareas por estado de finalización</h3>
                    <div className="h-64 w-full flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={tasksByStatus}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={90}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {tasksByStatus.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? '#10B981' : '#E5E7EB'} /> // Green for finalized, Gray for incomplete
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Advanced Reports Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Completion Rate */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center">
                    <span className="text-gray-500 text-xs mb-2 uppercase tracking-wide font-semibold">Tasa de completado</span>
                    <div className="relative w-24 h-24">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={completionRate > 70 ? '#10B981' : completionRate > 40 ? '#F59E0B' : '#EF4444'} strokeWidth="3" strokeDasharray={`${completionRate}, 100`} />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-800">{completionRate}%</span>
                    </div>
                </div>

                {/* Priority Distribution */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <span className="text-gray-500 text-xs mb-3 uppercase tracking-wide font-semibold block">Distribución por prioridad</span>
                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={priorityData} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                    {priorityData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Velocity */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-1 mb-3">
                        <FiTrendingUp className="text-green-500" size={14} />
                        <span className="text-gray-500 text-xs uppercase tracking-wide font-semibold">Velocidad semanal</span>
                    </div>
                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={velocityData}>
                                <XAxis dataKey="week" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                <Bar dataKey="completadas" fill="#10B981" radius={[3, 3, 0, 0]} barSize={16} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Burndown Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <FiTrendingDown className="text-blue-500" size={14} />
                    <h3 className="text-sm font-medium text-gray-700">Burndown (últimos 30 días)</h3>
                </div>
                <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={burndownData}>
                            <defs>
                                <linearGradient id="burndownGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#9CA3AF' }} interval={4} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                            <Area type="monotone" dataKey="remaining" stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#burndownGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tasks by Assignee */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-6">Total de próximas tareas por responsable</h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={tasksByAssignee} margin={{ bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    interval={0}
                                    tick={<CustomUserTick users={users} />}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                <Tooltip cursor={{ fill: '#f9fafb' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                <Bar dataKey="count" fill="#A78BFA" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Timeline */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-6">Carga de trabajo a lo largo del tiempo</h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={tasksOverTime}>
                                <defs>
                                    <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#818CF8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#818CF8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                <Area type="monotone" dataKey="count" stroke="#818CF8" fillOpacity={1} fill="url(#colorTasks)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardView;
